import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Sparkles, Copy, Trash2, Palette, Eye, Save, AlertTriangle, Globe } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { api, fileUrl } from '../../lib/api';
import { formatDate } from '../../components/Shared';
import { toast } from 'sonner';

// ---- Share Image Preview (Facebook-style; preparation only — no live OG changes
// happen until the final public domain is confirmed and connected) ----
const SHARE_PAGES = {
  default: { label: 'Default (all pages)', image_key: 'default_image' },
  homepage: { label: 'Homepage', image_key: 'homepage_image' },
  issue_tracker: { label: 'Ward 8 Public Issue Tracker', image_key: 'issue_tracker_image' },
  savings_tracker: { label: 'Savings Tracker', image_key: 'savings_tracker_image' },
  newsroom: { label: 'Newsroom', image_key: 'newsroom_image' },
};

const SHARE_DEFAULTS = {
  default_title: 'Vote Steve Lesperance — Ward 8 Councillor, Kawartha Lakes',
  default_description: 'Report it. Map it. Track it. Get answers. The Ward 8 Public Issue Tracker and a practical plan for better service and better value.',
  default_image: '/api/uploads/ward8-aerial.jpg',
  issue_tracker_image: '/api/uploads/ward8-aerial.jpg',
  savings_tracker_image: '',
  newsroom_image: '',
  homepage_image: '',
  final_domain: '',
};

const SharePreviewSection = () => {
  const [form, setForm] = useState(null);
  const [page, setPage] = useState('default');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/admin/settings').then((r) => setForm({ ...SHARE_DEFAULTS, ...(r.data?.social_sharing || {}) })).catch(() => setForm({ ...SHARE_DEFAULTS }));
  }, []);

  if (!form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const pg = SHARE_PAGES[page];
  const image = form[pg.image_key] || form.default_image;
  const domainConnected = !!(form.final_domain || '').trim();
  const domainShown = (form.final_domain || 'final-domain-not-set-yet.com').replace(/^https?:\/\//, '').replace(/\/$/, '');

  const save = async () => {
    setBusy(true);
    try {
      await api.put('/settings', { key: 'social_sharing', value: form });
      toast.success('Share preview settings saved (preparation only — nothing goes live until the final domain is connected)');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setBusy(false);
  };

  return (
    <Card className="mb-6" id="share-preview" data-testid="share-preview-section">
      <CardContent className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
          <p className="font-serif font-bold text-lg text-[#1a2744] flex items-center gap-2"><Eye className="h-4 w-4 text-[#C9A84C]" /> Share Image Preview</p>
          <Button asChild variant="outline" size="sm" className="font-bold"><Link to="/admin/designs" data-testid="share-preview-designs-link"><Palette className="h-4 w-4 mr-1" /> Design Manager</Link></Button>
        </div>
        <p className="text-xs text-slate-500 mb-3">Preview how the Facebook link card will look once the final public domain is connected. Preparation only — the live site's OpenGraph tags stay untouched until the domain is confirmed.</p>

        {!domainConnected && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 mb-4 flex items-start gap-2" data-testid="share-preview-domain-warning">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span><strong>Final domain not connected yet.</strong> This is a preview only — live Facebook link previews will use these settings once the final public domain is confirmed and connected. Nothing changes on the live site in the meantime.</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Page</label>
              <Select value={page} onValueChange={setPage}>
                <SelectTrigger className="h-11" data-testid="share-preview-page-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(SHARE_PAGES).map(([v, p]) => <SelectItem key={v} value={v}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Share title</label>
              <Input value={form.default_title || ''} onChange={(e) => set('default_title', e.target.value)} className="h-11" data-testid="share-preview-title" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Share description</label>
              <Textarea value={form.default_description || ''} onChange={(e) => set('default_description', e.target.value)} rows={2} data-testid="share-preview-description" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Share image for this page (uploaded image path)</label>
              <Input value={form[pg.image_key] || ''} onChange={(e) => set(pg.image_key, e.target.value)} placeholder="/api/uploads/… — leave blank to use the default image" className="h-11" data-testid="share-preview-image-input" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1 flex items-center gap-1"><Globe className="h-3.5 w-3.5" /> Final public domain</label>
              <Input value={form.final_domain || ''} onChange={(e) => set('final_domain', e.target.value)} placeholder="e.g. stevelesperance.com — leave blank until confirmed" className="h-11" data-testid="share-preview-domain-input" />
            </div>
            <Button onClick={save} disabled={busy} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="share-preview-save">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />} Save Share Settings
            </Button>
          </div>

          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-2">Facebook link preview — {pg.label}</p>
            <div className="max-w-md rounded-xl border border-slate-300 overflow-hidden bg-white shadow-sm" data-testid="share-preview-card">
              {image ? (
                <img src={fileUrl(image)} alt="Share preview" className="w-full h-52 object-cover" data-testid="share-preview-image" />
              ) : (
                <div className="w-full h-52 bg-slate-100 flex items-center justify-center text-slate-400 text-sm" data-testid="share-preview-image-empty">No image set — the default share image will be used</div>
              )}
              <div className="p-3 bg-slate-50 border-t border-slate-200">
                <p className="text-[10px] uppercase tracking-wide text-slate-500" data-testid="share-preview-card-domain">{domainShown}</p>
                <p className="font-bold text-[#1a2744] text-sm mt-0.5 leading-snug" data-testid="share-preview-card-title">{form.default_title}</p>
                <p className="text-xs text-slate-500 mt-0.5 line-clamp-2" data-testid="share-preview-card-description">{form.default_description}</p>
              </div>
            </div>
            <p className="text-[11px] text-slate-400 mt-2">Active social share images can also be versioned in the Design Manager (Social Share Image slot).</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function AdminSocial() {
  const [drafts, setDrafts] = useState([]);
  const [platform, setPlatform] = useState('facebook');
  const [topic, setTopic] = useState('');
  const [tone, setTone] = useState('positive and community-focused');
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/admin/social-drafts').then((r) => setDrafts(r.data || []));
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!topic.trim()) { toast.error('Describe the topic first'); return; }
    setBusy(true);
    try {
      await api.post('/admin/social/generate', { platform, topic, tone });
      toast.success('Draft generated');
      setTopic('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setBusy(false);
    }
  };

  const copy = (text) => { navigator.clipboard?.writeText(text); toast.success('Copied to clipboard'); };

  return (
    <div data-testid="admin-social">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h1 className="font-serif font-black text-2xl text-[#1a2744]">Social Sharing</h1>
        <Button asChild variant="outline" size="sm" className="font-bold"><Link to="/admin/designs" data-testid="social-designs-link"><Palette className="h-4 w-4 mr-1" /> Design Manager</Link></Button>
      </div>
      <p className="text-sm text-slate-500 mb-4">Generate on-message social media drafts with AI, then copy them into Facebook, X, or Instagram.</p>

      <SharePreviewSection />

      <Card className="mb-6 border-[#CFE0FF] bg-[#EAF2FF]"><CardContent className="p-5 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Platform</label>
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger className="h-11 bg-white" data-testid="social-platform-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="facebook">Facebook</SelectItem>
                <SelectItem value="x">X (Twitter)</SelectItem>
                <SelectItem value="instagram">Instagram</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Tone</label>
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger className="h-11 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="positive and community-focused">Positive & community-focused</SelectItem>
                <SelectItem value="serious and accountability-focused">Serious & accountability-focused</SelectItem>
                <SelectItem value="urgent call to action">Urgent call to action</SelectItem>
                <SelectItem value="warm and personal">Warm & personal</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Topic</label>
          <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Reminder: Council budget meeting this Tuesday — residents can watch live" className="h-11 bg-white" data-testid="social-topic-input" />
        </div>
        <Button onClick={generate} disabled={busy} className="h-11 bg-[#1a2744] text-white font-bold btn-press" data-testid="social-generate-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />} Generate Draft
        </Button>
      </CardContent></Card>

      <div className="space-y-3">
        {drafts.map((d) => (
          <Card key={d.id} data-testid="social-draft-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between gap-2 mb-2">
                <p className="text-xs font-bold uppercase tracking-wider text-[#C9A84C]">{d.platform} · {formatDate(d.created_at)}</p>
                <div className="flex gap-1">
                  <button onClick={() => copy(d.content)} className="p-2 rounded-lg hover:bg-[#EAF2FF] text-[#1a2744]" aria-label="Copy" data-testid="social-copy-button"><Copy className="h-4 w-4" /></button>
                  <button onClick={async () => { await api.delete(`/admin/social-drafts/${d.id}`); load(); }} className="p-2 rounded-lg hover:bg-red-50 text-red-600" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{d.content}</p>
            </CardContent>
          </Card>
        ))}
        {drafts.length === 0 && <Card><CardContent className="p-8 text-center text-slate-500">No drafts yet — generate your first post above.</CardContent></Card>}
      </div>
    </div>
  );
}
